/**
 * Automation hooks.
 *
 * Playwright cannot speak into a microphone or hold a pointer lock, so the
 * end-to-end test drives the same code paths the player does through this
 * surface: it sends real `cast` messages with real gesture point lists to the
 * real server, and the server grades them with the real engine. Nothing here
 * shortcuts a judgement or fabricates a result — it only replaces the physical
 * input devices.
 *
 * Exposed on `window.__spell` in every build; it is inert unless called.
 */
import { C2S, type PracticeControlMsg } from '@spell/network-protocol';
import type { GesturePoint } from '@spell/shared-types';
import { net, SpellNet } from './networking/net';
import { useNet } from './networking/netStore';
import { useFlow } from './gameplay/flowStore';
import { useCastUi } from './gameplay/castState';
import { scene as bus } from './scene/sceneBus';
import { saveProfile } from './gameplay/persistence';
import { buildSpellLocally } from './crafting/buildSpell';
import type { Spell } from '@spell/spell-schema';

export interface TestApi {
  /** Current app screen. */
  screen(): string;
  /** Coarse room snapshot. */
  snapshot(): {
    /** 'duel' or 'practice'. */
    mode: string;
    phase: string;
    round: number;
    selfHp: number;
    oppHp: number;
    selfMana: number;
    /**
     * Where the two duellists are standing. Keyboard events a test dispatches
     * are real input, but nothing else on this surface reveals whether they
     * moved anyone — and footwork is only meaningful if it changes where a
     * shot has to be aimed.
     */
    selfX: number;
    selfZ: number;
    oppX: number;
    oppZ: number;
    slots: string[];
    projectiles: number;
    incoming: number;
    nearestEta: number;
    /**
     * Seconds left in the impact hold of an incoming shot that has already
     * landed, or 0 when nothing is holding. Non-zero means the last-chance
     * parry beat is running right now.
     */
    holdingIn: number;
    fps: number;
    matchOver: boolean;
  };
  /** Skip the tutorial by installing a ready-made parry spell. */
  installParrySpell(spell: Spell): void;
  /** Install a ready-made lobby attack spell. */
  installAttackSpell(spell: Spell): void;
  /**
   * Forge a parry spell through the real pipeline — server interpretation,
   * fixed balance rules, real gesture template — and store it. Used by the
   * end-to-end match test so it exercises the production path rather than a
   * hand-written fixture.
   */
  craftParry(input: { chant: string; description: string }): Promise<Spell>;
  /**
   * The same, for the attack spell the lobby now requires before a duel. Both
   * go through `buildSpellLocally`, which is the exact call the lobby's forge
   * makes when a player presses confirm.
   */
  craftAttack(input: { chant: string; description: string }): Promise<Spell>;
  /** Force keyboard chant mode. */
  useTypedChant(text: string): void;
  /**
   * *Perform* a spell: replay its registered sigil and speak its chant.
   *
   * The id says which spell to imitate, not which one to cast — nothing tells
   * the server that. It receives the same evidence a player produces and
   * identifies the spell itself, so a test that asks for the wrong shape gets
   * the wrong spell, exactly as a player would.
   *
   * `onsetOffsetMs` backdates the reported speech onset. A real cast is always
   * sent later than the moment the player started speaking — the sigil still
   * has to be drawn and the key released — and that gap is the whole reason
   * the impact hold exists, so the automation surface has to be able to
   * reproduce it. It shifts only the reported timestamp, which the server
   * validates and clamps exactly as it does for a human.
   */
  cast(
    spellId: string,
    opts?: { transcript?: string; jitter?: number; onsetOffsetMs?: number },
  ): void;
  /** Perform the parry spell against the nearest incoming projectile. */
  parry(opts?: { transcript?: string; jitter?: number; onsetOffsetMs?: number }): void;
  /** Seconds until the nearest incoming projectile lands, or Infinity. */
  incomingEta(): number;
  /** Submit a signature spell during a craft phase. */
  submitSignature(input: { chant: string; description: string; slot?: number }): void;
  /** Vote for a rematch. */
  rematch(keepSpells: boolean): void;
  /** Adjust the practice range's dummy. Ignored outside a practice room. */
  practiceControl(patch: PracticeControlMsg): void;
  /** Mark ready on the connection-check screen. */
  ready(): void;
  /** Latest judgement returned by the server. */
  lastResult(): unknown;
}

declare global {
  interface Window {
    __spell?: TestApi;
  }
}

/**
 * Replay a stored template as a fresh attempt, with optional jitter so a test
 * can exercise Weak/Normal/Perfect rather than always drawing a perfect copy.
 */
function replayTemplate(spell: Spell | undefined, jitter: number): GesturePoint[] {
  if (!spell?.gesture) {
    // No template: emit a plain right-to-left stroke so the payload is valid.
    return Array.from({ length: 24 }, (_, i) => ({ x: -1 + (i / 23) * 2, y: 0, t: i * 12, s: 0 }));
  }
  return spell.gesture.points.map((p, i) => ({
    x: p.x + (Math.sin(i * 12.9898) * jitter),
    y: p.y + (Math.cos(i * 78.233) * jitter),
    t: i * 10,
    s: p.s,
  }));
}

/**
 * A distinct two-stroke sigil per `variant`, registered twice.
 *
 * Every crafted test spell needs its *own* shape. Since nothing selects a spell
 * any more, the sigil is what identifies it, and two fixtures sharing one shape
 * would be mutually uncastable the moment a cast carried no transcript — which
 * is exactly the state the headless suites run in. Giving each a real shape
 * keeps the automation honest about what a player would experience.
 */
function sampleSigil(variant: number): GesturePoint[][] {
  const line = (
    from: [number, number],
    to: [number, number],
    n: number,
    stroke: number,
    t0: number,
  ): GesturePoint[] =>
    Array.from({ length: n }, (_, i) => {
      const p = i / (n - 1);
      return {
        x: from[0] + (to[0] - from[0]) * p,
        y: from[1] + (to[1] - from[1]) * p,
        t: t0 + i * 12,
        s: stroke,
      };
    });
  const arc = (dir: number, stroke: number, t0: number): GesturePoint[] =>
    Array.from({ length: 26 }, (_, i) => {
      const a = dir * (i / 25) * Math.PI * 2;
      return { x: Math.cos(a) * 0.5, y: 0.3 + Math.sin(a) * 0.5, t: t0 + i * 12, s: stroke };
    });

  // Four shapes that differ in form, stroke count and stroke direction, not
  // just in slope — the engine scores all three, and shapes separated only by
  // a tilt came out at 0.83 against each other, over the distinctness limit.
  const shapes: GesturePoint[][] = [
    // 0 — sweep right, then a loop anticlockwise.
    [...line([-0.8, -0.4], [0.8, -0.4], 18, 0, 0), ...arc(-1, 1, 240)],
    // 1 — a cross: down the middle, then a bar left to right.
    [...line([0, -0.8], [0, 0.8], 18, 0, 0), ...line([-0.7, 0.1], [0.7, 0.1], 16, 1, 240)],
    // 2 — a V, then a bar drawn the other way.
    [
      ...line([-0.7, -0.6], [0, 0.7], 14, 0, 0),
      ...line([0, 0.7], [0.7, -0.6], 14, 0, 180),
      ...line([0.7, -0.7], [-0.7, -0.7], 16, 1, 380),
    ],
    // 3 — a zigzag, then a loop clockwise.
    [
      ...line([-0.8, 0.6], [0.8, 0.6], 12, 0, 0),
      ...line([0.8, 0.6], [-0.8, -0.2], 14, 0, 150),
      ...line([-0.8, -0.2], [0.8, -0.2], 12, 0, 330),
      ...arc(1, 1, 500),
    ],
  ];
  const sample = shapes[variant % shapes.length];
  return [sample, sample];
}

function nearestIncoming() {
  let best: (typeof bus.projectiles)[number] | null = null;
  for (const p of bus.projectiles) {
    if (!p.incoming) continue;
    if (best === null || p.eta < best.eta) best = p;
  }
  return best;
}

export function installTestHooks() {
  if (typeof window === 'undefined') return;

  const api: TestApi = {
    screen: () => useFlow.getState().screen,

    snapshot: () => {
      const state = useNet.getState();
      const nearest = nearestIncoming();
      return {
        mode: state.mode,
        phase: state.phase,
        round: state.round,
        selfHp: bus.selfHp,
        oppHp: bus.oppHp,
        selfMana: bus.selfMana,
        selfX: bus.selfX,
        selfZ: bus.selfZ,
        oppX: bus.oppX,
        oppZ: bus.oppZ,
        slots: state.slots,
        projectiles: bus.projectiles.length,
        incoming: bus.projectiles.filter((p) => p.incoming).length,
        nearestEta: nearest ? nearest.eta : Number.POSITIVE_INFINITY,
        holdingIn: bus.projectiles.reduce(
          (best, p) => (p.incoming && p.resolveIn > best ? p.resolveIn : best),
          0,
        ),
        fps: bus.fps,
        matchOver: state.matchResult !== null,
      };
    },

    craftParry: async ({ chant, description }) => {
      const built = await buildSpellLocally({
        type: 'parry',
        chant,
        description,
        gestureSamples: sampleSigil(0),
        ownerId: 'local',
        id: `parry-e2e-${chant.replace(/\s+/g, '-').toLowerCase()}`,
      });
      api.installParrySpell(built.spell);
      return built.spell;
    },

    craftAttack: async ({ chant, description }) => {
      const built = await buildSpellLocally({
        type: 'signature',
        chant,
        description,
        gestureSamples: sampleSigil(1),
        ownerId: 'local',
        id: `sig-e2e-${chant.replace(/\s+/g, '-').toLowerCase()}`,
      });
      api.installAttackSpell(built.spell);
      return built.spell;
    },

    installParrySpell: (spell) => {
      saveProfile({ tutorialDone: true, parrySpell: spell });
      useFlow.setState({ tutorialDone: true, parrySpell: spell, screen: 'lobby' });
    },

    installAttackSpell: (spell) => {
      saveProfile({ attackSpell: spell });
      useFlow.setState({ attackSpell: spell });
    },

    useTypedChant: (text) => {
      useCastUi.getState().setFallback(true);
      useCastUi.getState().setTypedChant(text);
    },

    cast: (spellId, opts = {}) => {
      const spell = useNet.getState().ownSpells[spellId];
      const jitter = opts.jitter ?? 0.02;
      net.send(C2S.CAST, {
        transcript: opts.transcript ?? spell?.chant ?? '',
        gesture: replayTemplate(spell, jitter),
        speechStartAt: SpellNet.now() + (opts.onsetOffsetMs ?? 0),
        yaw: bus.yaw,
        pitch: bus.pitch,
      });
    },

    parry: (opts = {}) => {
      const state = useNet.getState();
      const parryId = state.slots.find((id) => state.ownSpells[id]?.type === 'parry');
      if (!parryId) return;
      api.cast(parryId, opts);
    },

    incomingEta: () => {
      const nearest = nearestIncoming();
      return nearest ? nearest.eta : Number.POSITIVE_INFINITY;
    },

    submitSignature: ({ chant, description, slot }) => {
      const target = slot ?? useNet.getState().craft?.slot ?? 0;
      // A shape of its own per slot. The room refuses a spell whose sigil is
      // too close to one the player already owns — it would be uncastable —
      // so the automation has to draw something new each craft phase, exactly
      // as a player does. `+ 2` keeps it clear of the two lobby fixtures.
      net.send(C2S.SUBMIT_SPELL, {
        type: 'signature',
        chant,
        description,
        gestureSamples: sampleSigil(target + 2),
        slot: target,
      });
    },

    rematch: (keepSpells) => net.send(C2S.REMATCH, { keepSpells }),

    practiceControl: (patch) => net.send(C2S.PRACTICE_CONTROL, patch),

    ready: () => net.send(C2S.READY),

    lastResult: () => useCastUi.getState().lastResult,
  };

  window.__spell = api;
}
